/**
 * useConversationContextMenu Hook (Audit)
 *
 * Manages state for the Conversation Context modal triggered from the
 * Context button on each audit item card.
 *
 * When the user clicks "Context", this hook performs a content search
 * using the audit item's `subjectId` (from kustoReference) to retrieve
 * the full `contentMetadata.metadata` from the Content Search API. This
 * metadata (e.g. world, server) is then used by the ConversationContextModal
 * to call the contextual-messages API — matching how Moderation's
 * conversation context flow works.
 */

import type { ContentCardItem } from '@content-search/domain';
import { useContentSearch } from '@content-search/hooks';
import { createContentCardItem } from '@content-search/utils';
import type { DomainError } from '@core/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { GraphQLAuditItem } from '../domain/graphql-types';

/** Parameters for the conversation context menu hook */
interface ConversationContextMenuParams {
  communityId: string;
}

/** All state & handlers the component needs for the context modal */
export interface ConversationContextMenuState {
  /** Whether the conversation context modal is open */
  isContextModalOpen: boolean;
  /** Open the context modal for a specific audit item */
  handleOpenContextModal: (item: GraphQLAuditItem) => void;
  /** Close the context modal */
  handleCloseContextModal: () => void;
  /** Props to pass directly to ConversationContextModal (only valid when open) */
  contextModalProps: {
    communityId: string;
    subjectId: string;
    topicName: string;
    metadata: Record<string, unknown>;
    selectedMessage: ContentCardItem | undefined;
    isSearchingContent: boolean;
    contentSearchErrors: DomainError[] | null;
  };
}

/**
 * Hook that manages the Conversation Context modal state for audit items.
 *
 * Performs a content search by subjectId (same as Moderation) to obtain
 * the metadata needed for the contextual-messages API, then opens the modal
 * with that search-derived metadata and selectedMessage.
 */
export function useConversationContextMenu({ communityId }: ConversationContextMenuParams): ConversationContextMenuState {
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [contextItem, setContextItem] = useState<GraphQLAuditItem | null>(null);
  const [searchDerivedMetadata, setSearchDerivedMetadata] = useState<Record<string, unknown> | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ContentCardItem | undefined>();

  const { search, searchResult, isSearching: isContentSearching, errors: contentSearchErrors } = useContentSearch();

  // When content search completes, extract metadata and build selectedMessage
  useEffect(() => {
    if (!isContextModalOpen || !searchResult?.results?.length) return;

    const firstResult = searchResult.results[0];
    const contentMetadata = firstResult.contentMetadata;

    // Extract metadata from content search result (world, server, etc.)
    if (contentMetadata && 'metadata' in contentMetadata && contentMetadata.metadata) {
      setSearchDerivedMetadata(contentMetadata.metadata as Record<string, unknown>);
    }

    // Build a ContentCardItem for highlighting the target message in the modal
    const cardItem = createContentCardItem(firstResult, 0);

    setSelectedMessage(cardItem);
  }, [searchResult, isContextModalOpen]);

  const contextModalProps = useMemo(
    () => ({
      communityId,
      subjectId: contextItem?.kustoReference?.subjectId ?? '',
      topicName: contextItem?.contentSnapshot?.topic ?? '',
      // Use metadata from content search result; empty object until search completes
      metadata: searchDerivedMetadata ?? {},
      selectedMessage,
      isSearchingContent: isContentSearching,
      contentSearchErrors,
    }),
    [communityId, contextItem, searchDerivedMetadata, selectedMessage, isContentSearching, contentSearchErrors]
  );

  const handleOpenContextModal = useCallback(
    (item: GraphQLAuditItem) => {
      const subjectId = item.kustoReference?.subjectId;

      if (!subjectId) return;

      // Store the target item and reset state for fresh search
      setContextItem(item);
      setSearchDerivedMetadata(null);
      setSelectedMessage(undefined);
      setIsContextModalOpen(true);

      // Perform content search by subjectId — same approach as Moderation
      search({ communityId, subjectId, maxResultsPerBatch: 1 }, 'network-only');
    },
    [communityId, search]
  );

  const handleCloseContextModal = useCallback(() => {
    setIsContextModalOpen(false);
  }, []);

  return {
    isContextModalOpen,
    handleOpenContextModal,
    handleCloseContextModal,
    contextModalProps,
  };
}
